/**
 * PaymentMethodSection — Billing step (Wave 4 native, resurrected from
 * b931d8142^).
 *
 * Card-based selector over the payment methods the BE actually offers for this
 * session (icon + label + one-line hint), reading/writing
 * `billingSlice.paymentMethod`. No Stripe redirect here — that happens at Place
 * Order (Summary + place-order integration). This section is selection UX only.
 *
 * The availability list is now CATALOGUE-DRIVEN: `methods` comes
 * from `resolveAvailablePaymentMethods` over the v2 instruction options' BILLING
 * flavor (`useBillingPaymentMethods`), replacing the hardcoded three. An admin
 * (whose mimetype strips direct-payment options) therefore no longer sees
 * card/SEPA cards that place-order would reject. Legacy's remaining card
 * behaviours are now ported too: the invoice gate (admin / org permission /
 * priced invoice product) with its lock badge and subscribe upsell, the method
 * fee, the "free for subscribed" badge on a priced invoice, and the
 * save-as-default switch.
 *
 * §11.1 — the BE `isDefault` preselect arrives here as part of `value` (the page
 * computes `paymentMethod ?? defaultPaymentMethod` at RENDER). This component
 * NEVER calls `onChange` on its own, so a preselect cannot dirty the draft.
 */

import React from 'react'
import { Stack, Typography } from '@mui/material'
import CreditCard from '@mui/icons-material/CreditCard'
import AccountBalance from '@mui/icons-material/AccountBalance'
import ReceiptLong from '@mui/icons-material/ReceiptLong'
import { useTranslation } from 'react-i18next'

import LockOutlined from '@mui/icons-material/LockOutlined'

import { GRAY_900 } from 'constants/styling/theme'
import { PfInlineLoading } from '../../../components/PfInlineLoading.component'
import type { PaymentMethod } from 'models/purchaseFlowRevamp'
import { MUIBadge } from 'components/common/MUIBadge'
import { PreferredPaymentMethodSwitch } from 'components/common/PreferredPaymentMethodSwitch'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { formatPrice } from 'utils/price/priceFormatter'
import { productKindIsPreferredPaymentMethod } from 'utils/typeguards'

import { MUICheckCard } from 'components/common/MUICards'
import { SubscriptionUpsellNote } from './SubscriptionUpsellNote.component'
import type { PaymentMethodOffer } from '../billing.helpers'

interface Props {
  /** EFFECTIVE method — the slice value, or the BE `isDefault` preselect. */
  value: PaymentMethod | undefined
  onChange: (next: PaymentMethod | undefined) => void
  /** Methods to render, in BE order (`resolveAvailablePaymentMethods`). */
  methods: ReadonlyArray<PaymentMethod>
  /** Per-method option + fee + permission (`resolvePaymentMethodOffers`). */
  offers?: ReadonlyArray<PaymentMethodOffer>
  /** Catalogue currency, for the fee label. */
  currency?: string
  /** Only clients may set a default payment method (legacy `showSaveOption`). */
  canSaveDefault?: boolean
  /** Fired on a real user pick, for the funnel event. */
  onMethodPicked?: (method: PaymentMethod) => void
  /** True while the instruction options are in flight — show a spinner rather
   *  than flashing the fallback three and having them change under the user. */
  isLoading?: boolean
}

const METHOD_ICON: Record<PaymentMethod, React.ReactNode> = {
  card: <CreditCard fontSize="small" />,
  sepa: <AccountBalance fontSize="small" />,
  invoice: <ReceiptLong fontSize="small" />,
}

export const PaymentMethodSection: React.FC<Props> = ({
  value,
  onChange,
  methods,
  offers,
  currency,
  canSaveDefault = false,
  onMethodPicked,
  isLoading = false,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'badges', 'preferred_payment_method'])

  return (
    <SectionedBorderBox variant="flat"
      title={t('purchase_flow_revamp:billing.payment_method.section_title')}
      description={t('purchase_flow_revamp:billing.payment_method.section_description')}
    >
      {isLoading &&
        <PfInlineLoading message={t('purchase_flow_revamp:billing.payment_method.loading')} />
      }

      {!isLoading &&
        <Stack gap={0.8} role="radiogroup" aria-label={t('purchase_flow_revamp:billing.payment_method.section_title')}>
          {methods.map((method) => {
            const offer = offers?.find((candidate) => candidate.method === method)
            // Absent offers (a caller that hasn't been threaded yet) must not
            // silently lock every card, so default to allowed.
            const allowed = offer?.allowed ?? true
            const fee = offer?.amount ?? 0
            const kind = offer?.option?.kind
            const showSwitch = canSaveDefault && allowed && !!kind && productKindIsPreferredPaymentMethod(kind)

            return (
              <MUICheckCard
                key={method}
                isRadio
                checked={allowed && value === method}
                disabled={!allowed}
                onCheck={() => {
                  // Redundant with `disabled` above — the card already refuses to
                  // fire when disabled. Kept so the handler is safe on its own;
                  // either guard alone blocks a locked pick, both must stay.
                  if (!allowed) return
                  onChange(method)
                  onMethodPicked?.(method)
                }}
                icon={METHOD_ICON[method]}
                title={t(`purchase_flow_revamp:billing.payment_method.method_${method}`)}
                description={
                  <Stack gap={0.4}>
                    <span>{t(`purchase_flow_revamp:billing.payment_method.method_${method}_hint`)}</span>
                    {/* Not `expandedContent`: that only renders while SELECTED, and a
                        locked card can never be selected. */}
                    {!allowed && <SubscriptionUpsellNote />}
                  </Stack>
                }
                trailing={
                  <Stack direction="row" alignItems="center" gap={1.2}>

                    {/* A priced invoice is the one the plan would make free. */}
                    {method === 'invoice' && fee > 0 &&
                      <MUIBadge color="coral" label={t('badges:free_for_subscribed')} />
                    }

                    {!allowed &&
                      <MUIBadge
                        color="coral"
                        label={t('preferred_payment_method:badge_only_subscribed')}
                        icon={<LockOutlined fontSize="small" />}
                      />
                    }

                    {fee > 0 &&
                      <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
                        {formatPrice(fee, currency)}
                      </Typography>
                    }

                    {showSwitch && <PreferredPaymentMethodSwitch paymentMethod={kind} />}

                  </Stack>
                }
                data-cy={`pf-revamp-billing-payment-${method}`}
              />
            )
          })}
        </Stack>
      }
    </SectionedBorderBox>
  )
}
